import { Fragment } from "react/jsx-runtime";
import {
  MiModalConfirm,
  PrioritySchedulerEventViewComponentProps,
  PrioritySchedulerLocation,
} from "@miplatform/components";
import {
  ATTEMPT_STATUSES,
  HTTP_STATUSES,
  PERMISSIONS,
  PRIORITY_SCHEDULER_SOURCE_TYPES,
} from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  IAttemptListItemResponseForWorkOrder,
  WorkOrderStatus,
} from "@miplatform/types";
import { isToday } from "@miplatform/utils";
import {
  mdiAccountCancel,
  mdiCalendarImportOutline,
  mdiFormatListBulleted,
  mdiHandBackRightOutline,
  mdiLockOpenOutline,
  mdiLockOutline,
  mdiPlay,
} from "@mdi/js";
import { useTheme } from "@mui/material";
import { usePermissions } from "@mibuildweb/hooks";
import { WorkOrderForcedToNextModal } from "./modals/WorkOrderForcedToNextModal/WorkOrderForcedToNextModal";
import {
  MiWorkOrderCard,
  MiWorkOrderCardProps,
} from "../../../components/MiWorkOrderCard/MiWorkOrderCard";
import { SchedulerViewType, useCalendarContext } from "../../../providers";
import { HoldAttemptFormData } from "../NhcCalendar.types";
import { WorkOrderHoldModal } from "../WorkOrderHoldModal/WorkOrderHoldModal";

const finalizedOrHeldWorkOrderStatuses = [
  WorkOrderStatus.Complete,
  WorkOrderStatus.Cancelled,
  WorkOrderStatus.Hold,
];

type NHCWorkOrderCardProps = MiWorkOrderCardProps &
  Pick<PrioritySchedulerEventViewComponentProps, "source" | "selectedUserId">;

export const NHCWorkOrderCard = (props: NHCWorkOrderCardProps) => {
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const { attempt, source, selectedUserId, ...cardProps } = props;
  const { attemptStatus, assignment: { workOrder = {} } = {} } = attempt;
  const [isHoldModalOpen, handleHoldModalOpen, handleHoldModalClose] =
    useBoolean();
  const [isStartModalOpen, handleStartModalOpen, handleStartModalClose] =
    useBoolean();
  const [
    isUnassignModalOpen,
    handleUnassignModalOpen,
    handleUnassignModalClose,
  ] = useBoolean();
  const [
    isForceToNextModalOpen,
    handleForceToNextModalOpen,
    handleForceToNextModalClose,
  ] = useBoolean();
  const {
    isCalendarLoading,
    calendarSet,
    setSelectedAttempt,
    startWorkOrder,
    holdWorkOrder,
    unscheduleAttempt,
    getCalendarCounts,
    handleForceToNextAttempt,
    activeViewRef,
    updateAttempt,
    isAssigneeMode,
    assigneeModeUser,
    assignableUsers,
    isScheduler,
    setWorkOrderLock,
  } = useCalendarContext();

  const isFromTray = source?.type === PRIORITY_SCHEDULER_SOURCE_TYPES.TRAY;

  const canForceToNextFromScheduler =
    ![
      WorkOrderStatus.Complete,
      WorkOrderStatus.Cancelled,
      WorkOrderStatus.InProgress,
    ].includes(workOrder?.status as WorkOrderStatus) &&
    attemptStatus !== ATTEMPT_STATUSES.CLOSED &&
    isScheduler;

  const canForceToNext = isFromTray || canForceToNextFromScheduler;

  const isHoldHidden =
    [
      ATTEMPT_STATUSES.HOLD,
      ATTEMPT_STATUSES.CANCELLED,
      ATTEMPT_STATUSES.CLOSED,
    ].includes(attempt.attemptStatus) ||
    finalizedOrHeldWorkOrderStatuses.includes(
      workOrder?.status as WorkOrderStatus
    );

  const isStartHidden =
    [
      WorkOrderStatus.Complete,
      WorkOrderStatus.Cancelled,
      WorkOrderStatus.Hold,
      WorkOrderStatus.New,
      WorkOrderStatus.InProgress,
      WorkOrderStatus.Unassigned,
    ].includes(workOrder?.status as WorkOrderStatus) ||
    attemptStatus === ATTEMPT_STATUSES.CLOSED ||
    !isAssigneeMode;

  const isUnassignHidden =
    !attempt.assignedUserId ||
    attempt.attemptStatus === ATTEMPT_STATUSES.CANCELLED ||
    attempt.attemptStatus === ATTEMPT_STATUSES.CLOSED ||
    [...finalizedOrHeldWorkOrderStatuses, WorkOrderStatus.InProgress].includes(
      workOrder?.status as WorkOrderStatus
    ) ||
    !isScheduler;

  const isLocked = attempt.assignment?.workOrder?.isLocked;

  const canLockWorkOrder = hasPermission(PERMISSIONS.WORK_ORDER.LOCK);
  
  const isLockHidden =
    !canLockWorkOrder ||
    isLocked ||
    attempt.attemptStatus === ATTEMPT_STATUSES.NEW;
  const isUnlockHidden = !canLockWorkOrder || !isLocked;

  const menuItems = [
    {
      label: "Start",
      icon: mdiPlay,
      color: theme.palette.yellow.main,
      action: handleStartModalOpen,
      hide: isStartHidden,
      disabled: !isToday(attempt.scheduledStartDate),
    },
    {
      label: "Hold",
      icon: mdiHandBackRightOutline,
      color: theme.palette.purple.dark,
      action: handleHoldModalOpen,
      disabled: isLocked,
      hide: isHoldHidden,
    },
    {
      label: "Unassign",
      icon: mdiAccountCancel,
      color: theme.palette.red.main,
      action: handleUnassignModalOpen,
      disabled: isLocked,
      hide: isUnassignHidden,
    },
    {
      label: "View Details",
      icon: mdiFormatListBulleted,
      color: theme.palette.grey.darkerAccent as string,
      action: () => setSelectedAttempt(attempt),
    },
    {
      label: "Force To Next",
      icon: mdiCalendarImportOutline,
      color: theme.palette.primary.main,
      action: () => handleForceToNext(attempt, source),
      disabled: isLocked,
      hide: !canForceToNext,
    },
    {
      label: "Lock",
      icon: mdiLockOutline,
      color: theme.palette.red.main,
      action: () => setWorkOrderLock(attempt, true),
      hide: isLockHidden,
    },
    {
      label: "Unlock",
      icon: mdiLockOpenOutline,
      color: theme.palette.green.main,
      action: () => setWorkOrderLock(attempt, false),
      hide: isUnlockHidden,
    },
  ];

  const handleForceToNext = (
    attempt: IAttemptListItemResponseForWorkOrder,
    source?: PrioritySchedulerLocation
  ) => {
    if (activeViewRef.current === SchedulerViewType.Assignment) {
      if (!selectedUserId) {
        toastService.error({
          text: "Please select a user to force to next",
        });
        return;
      }
      handleForceToNextAttempt(attempt, selectedUserId);
      return;
    }

    if (isAssigneeMode) {
      handleForceToNextAttempt(attempt, assigneeModeUser?.id);
    } else {
      if (source?.type === PRIORITY_SCHEDULER_SOURCE_TYPES.TRAY) {
        handleForceToNextModalOpen();
        return;
      }
      handleForceToNextAttempt(attempt, attempt.assignedUserId);
    }
  };

  const submitHold = async (data: HoldAttemptFormData) => {
    const response = await holdWorkOrder(data);

    if (response.status === HTTP_STATUSES.OK) {
      handleHoldModalClose();
      getCalendarCounts();
      updateAttempt(response.data);
    }
  };

  const submitStart = async () => {
    const response = await startWorkOrder(attempt);

    if (response) {
      handleStartModalClose();
      updateAttempt(response);
    }
  };

  const submitForceToNext = (_userId: string) => {
    handleForceToNextModalClose();
    handleForceToNextAttempt(attempt, _userId);
  };

  return (
    <Fragment>
      <MiWorkOrderCard
        {...cardProps}
        attempt={attempt}
        menuItems={menuItems}
        isLocked={isLocked}
      />
      <WorkOrderHoldModal
        open={isHoldModalOpen}
        onClose={handleHoldModalClose}
        onHold={(data) => submitHold({ ...data, attempt })}
        calendarSet={calendarSet?.config ?? null}
        isLoading={!!isCalendarLoading}
      />
      <MiModalConfirm
        open={isUnassignModalOpen}
        titleIcon={{ path: mdiAccountCancel, color: theme.palette.red.main }}
        title="Unassign Work Order"
        description="Do you want to unassign this work order?"
        loading={!!isCalendarLoading}
        dismissOnBackdropClick={handleUnassignModalClose}
        handleNegative={handleUnassignModalClose}
        handlePositive={() => unscheduleAttempt(attempt)}
      />
      <MiModalConfirm
        open={isStartModalOpen}
        titleIcon={{ path: mdiPlay, color: theme.palette.yellow.main }}
        title="Start Work"
        description="Do you want to start this work?"
        loading={!!isCalendarLoading}
        dismissOnBackdropClick={handleStartModalClose}
        handleNegative={handleStartModalClose}
        handlePositive={submitStart}
      />
      <WorkOrderForcedToNextModal
        open={isForceToNextModalOpen}
        assignableUsers={assignableUsers}
        isLoading={!!isCalendarLoading}
        onClose={handleForceToNextModalClose}
        onConfirm={submitForceToNext}
      />
    </Fragment>
  );
};
